import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
    alt: "Financial district skyscrapers",
  },
  {
    src: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
    alt: "Capital markets and stock charts",
  },
  {
    src: "https://images.unsplash.com/photo-1531973576160-7125cd663d86?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
    alt: "Modern advisory office and partnership",
  },
];

const SLIDE_DURATION_MS = 6000;

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function Hero() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % SLIDES.length),
      SLIDE_DURATION_MS
    );
    return () => clearInterval(id);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden cgp-grain"
    >
      {/* Background slideshow */}
      <div className="absolute inset-0 overflow-hidden" data-testid="hero-slideshow">
        <AnimatePresence mode="wait" initial={false}>
          <motion.img
            key={index}
            src={SLIDES[index].src}
            alt={SLIDES[index].alt}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`hero-slide-${index}`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-[#051024]/95 via-[#051024]/80 to-[#0A1F44]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#051024]/40 via-transparent to-[#051024]/70" />
      </div>

      <div className="relative cgp-container pt-40 pb-24 md:pt-44 md:pb-28 flex flex-col min-h-[100svh] justify-between">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <motion.span
            variants={item}
            className="cgp-overline"
            data-testid="hero-overline"
          >
            Igniting Success Worldwide
          </motion.span>

          <motion.h1
            variants={item}
            data-testid="hero-headline"
            className="font-serif font-light text-white mt-6 text-[2.6rem] sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight"
          >
            Need <em className="not-italic text-[#C8A24A]">Capital &amp; Debt</em>
            <br />
            Funding? <span className="text-white/85">Connect with</span>
            <br />
            Catalyst Global Partners.
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-8 text-white/75 text-lg max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            A trusted partner for tailored financial solutions and high-growth
            FMCG brands. We bring capital, strategy and execution together —
            for businesses that mean business.
          </motion.p>

          <motion.div
            variants={item}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-services"
              onClick={() => navigate("/capital-funding")}
              className="cgp-btn-gold rounded-full px-7 py-3.5 inline-flex items-center gap-2 text-sm"
            >
              Explore Capital Funding
              <ArrowUpRight size={18} strokeWidth={2.2} />
            </button>
            <button
              data-testid="hero-cta-fmcg"
              onClick={() => navigate("/fmcg")}
              className="cgp-btn-ghost rounded-full px-7 py-3.5 inline-flex items-center gap-2 text-sm"
            >
              View FMCG Brands
            </button>
          </motion.div>

          {/* Slide indicators */}
          <motion.div
            variants={item}
            data-testid="hero-slide-dots"
            className="mt-12 flex items-center gap-3"
          >
            {SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-slide-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Show slide ${i + 1}`}
                className={`h-[3px] transition-all duration-500 ${
                  i === index
                    ? "w-10 bg-[#C8A24A]"
                    : "w-5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom strap */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="hidden md:flex items-end justify-between gap-10 mt-16"
        >
          <div className="flex gap-12">
            <Stat label="Industries Served" value="12+" />
            <Stat label="Capital Facilitated" value="$500M+" />
            <Stat label="Years of Expertise" value="20+" />
          </div>
          <button
            data-testid="hero-scroll-down"
            onClick={() => scrollTo("#about")}
            className="text-white/60 hover:text-[#C8A24A] transition-colors flex items-center gap-3 text-xs uppercase tracking-[0.25em]"
          >
            Scroll <ArrowDown size={14} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-[#C8A24A] text-3xl font-light leading-none">
        {value}
      </div>
      <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] mt-2">
        {label}
      </div>
    </div>
  );
}
